<template>
  <div>
    <BaseReportTable
      :resultados="resultados"
      :total-items="totalItems"
      :items-por-pagina="itemsPorPagina"
      :pagina-actual="paginaActual"
      :loading="loading"
      :headers="infoMoraHeaders"
      :es-operacion="esOperacion"
      @cambiar-pagina="$emit('cambiar-pagina', $event)"
      @cambiar-items-por-pagina="$emit('cambiar-items-por-pagina', $event)"
    >
      <template v-slot:item.legajo="{ item }">
        <a
          v-if="item.legajo"
          :href="getGestionarUrl(item.legajo)"
          target="_blank"
          class="legajo-link"
        >
          {{ item.legajo }}
        </a>
        <template v-else>-</template>
      </template>

      <template v-slot:item.operatoria_linea="{ item }">
        {{ item.operatoria_linea || '-' }}
      </template>

      <template v-slot:item.nombre_razon_social="{ item }">
        {{ item.nombre_razon_social ? item.nombre_razon_social.toUpperCase() : '-' }}
      </template>

      <template v-slot:item.cuit="{ item }">
        {{ item.cuit || '-' }}
      </template>
      <template v-slot:item.departamento="{ item }">
        {{ getDepartamentoNombre(item) }}
      </template>


      <template v-slot:item.localidad="{ item }">
        {{ item.localidad || '-' }}
      </template>

      <template v-slot:item.persona="{ item }">
        <span class="persona-text">{{ item.persona || '-' }}</span>
      </template>

      <template v-slot:item.fecha_ingreso="{ item }">
        {{ formatearFecha(item.fecha_ingreso) }}
      </template>

      <template v-slot:item.estado="{ item }">
        <span class="estado-text">{{ item.estado || '-' }}</span>
      </template>

      <template v-slot:item.monto="{ item }">
        {{ formatearMonto(item.monto) }}
      </template>

      <template v-slot:item.monto_anr="{ item }">
        {{ formatearMonto(item.monto_anr) }}
      </template>

      <template v-slot:item.monto_credito="{ item }">
        {{ calcularMontoCredito(item.monto, item.monto_anr) }}
      </template>

      <template v-slot:item.monto_otorgado="{ item }">
        {{ formatearMonto(item.monto) }}
      </template>

      <template v-slot:item.fecha_otorgamiento="{ item }">
        {{ item.fecha_otorgamiento || '-' }}
      </template>

      <template v-slot:item.cantidad_cuotas="{ item }">
        {{ item.cantidad_cuotas || '-' }}
      </template>

      <template v-slot:item.saldo_capital="{ item }">
        {{ formatearMonto(item.saldo_capital) }}
      </template>

      <template v-slot:item.monto_cobrado="{ item }">
        {{ formatearMonto(item.monto_cobrado) }}
      </template>

      <template v-slot:item.cantidad_cuotas_mora="{ item }">
        <span style="color: red; font-weight: bold">
          {{ item.cantidad_cuotas_mora || '-' }}
        </span>
      </template>

      <template v-slot:item.capital_vencido="{ item }">
        {{ formatearMonto(item.capital_vencido) }}
      </template>

      <template v-slot:item.dias_mora="{ item }">
        <v-chip
          v-if="item.dias_mora"
          small
          :color="obtenerColorDiasMora(item.dias_mora)"
          text-color="white"
        >
          {{ item.dias_mora }} días
        </v-chip>
        <span v-else>-</span>
      </template>

      <template v-slot:item.estado_morosidad="{ item }">
        <v-chip
          small
          :color="obtenerColorMorosidad(item.estado_morosidad)"
          text-color="white"
        >
          {{ item.estado_morosidad || 'S/E' }}
        </v-chip>
      </template>

      <template v-slot:item.porcentaje_mora="{ item }">
        <div class="d-flex align-center">
          <v-icon
            small
            :color="
              calcularPorcentajeMora(item.monto_mora, item.capital_vencido) ===
              0
                ? 'green'
                : 'orange'
            "
            class="mr-1"
          >
            {{
              calcularPorcentajeMora(item.monto_mora, item.capital_vencido) ===
              0
                ? 'mdi-check'
                : 'mdi-alert'
            }}
          </v-icon>
          <span>{{
            formatearPorcentaje(
              calcularPorcentajeMora(item.monto_mora, item.capital_vencido)
            )
          }}</span>
        </div>
      </template>

      <template v-slot:item.estado_manual="{ item }">
        {{ item.estado_manual || '-' }}
      </template>

      <template v-slot:item.monto_mora="{ item }">
        {{ formatearMonto(item.monto_mora) }}
      </template>

      <!-- Slot para la fila de totales - BaseReportTable maneja las primeras 8 columnas -->
      <template v-slot:totals-row>
        <!-- Columna 9: Monto ANR -->
        <td class="text-center">
          <span class="font-weight-bold">{{
            formatearMonto(totales.total_monto_anr)
          }}</span>
        </td>
        <!-- Columna 10: Monto Crédito -->
        <td class="text-center">
          <span class="font-weight-bold">{{
            calcularTotalMontoCredito(totales.total_monto, totales.total_monto_anr)
          }}</span>
        </td>
        <!-- Columna 11: Monto Otorgado -->
        <td class="text-center">
          <span class="font-weight-bold">{{
            formatearMonto(totales.total_monto)
          }}</span>
        </td>
        <!-- Columna 12: Fecha de Otorgamiento -->
        <td class="text-center"></td>
        <!-- Columna 13: Cantidad Cuotas -->
        <td class="text-center"></td>
        <!-- Columna 14: Saldo Capital -->
        <td class="text-center">
          <span class="font-weight-bold">{{
            formatearMonto(totales.total_saldo_capital)
          }}</span>
        </td>
        <!-- Columna 15: Monto Cobrado -->
        <td class="text-center">
          <span class="font-weight-bold">{{
            formatearMonto(totales.total_monto_cobrado)
          }}</span>
        </td>
        <!-- Columna 16: Cuotas en Mora -->
        <td class="text-center"></td>
        <!-- Columna 17: Capital Vencido -->
        <td class="text-center">
          <span class="font-weight-bold">{{
            formatearMonto(totales.total_capital_vencido)
          }}</span>
        </td>
        <!-- Columna 18: Monto en Mora -->
        <td class="text-center">
          <span class="font-weight-bold">{{
            formatearMonto(totales.total_monto_mora)
          }}</span>
        </td>
        <!-- Columna 19: Días de Mora -->
        <td class="text-center">
          <span class="font-weight-bold">{{
            formatearDiasMora(totales.promedio_dias_mora)
          }}</span>
        </td>
        <!-- Columna 20: Estado Morosidad -->
        <td class="text-center"></td>
        <!-- Columna 21: Porcentaje de Mora -->
        <td class="text-center"></td>
        <!-- Columna 22: Estado Manual -->
        <td class="text-center"></td>
      </template>
    </BaseReportTable>
  </div>
</template>

<script>
import BaseReportTable from './BaseReportTable.vue';
import { ReportesHelpers } from '@/utils/reportesHelpers';

export default {
  name: 'InfoMoraReportTable',
  components: {
    BaseReportTable,
  },
  props: {
    resultados: {
      type: Array,
      default: () => [],
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    itemsPorPagina: {
      type: Number,
      default: 10,
    },
    paginaActual: {
      type: Number,
      default: 1,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    headers: {
      type: Array,
      default: () => [],
    },
    esOperacion: {
      type: String,
      default: 'Info Mora (Financiamiento)',
    },
    totales: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    infoMoraHeaders() {
      return this.headers;
    },
  },
  methods: {
    getGestionarUrl(legajo) {
      const baseUrl = process.env.VUE_APP_GESTIONAR_URL || 'http://localhost:8080';
      return `${baseUrl}/gestionar/${legajo}`;
    },
    getDepartamentoNombre(item) {
      console.log('🔍 InfoMora - getDepartamentoNombre llamado:', {
        departamento: item.departamento,
        legajo: item.legajo,
        item: item
      });

      // Si el item ya tiene el campo departamento mapeado, usarlo
      if (item.departamento) {
        console.log('✅ Encontrado departamento directo:', item.departamento);
        return item.departamento;
      }

      // Intentar obtener departamento desde diferentes estructuras
      if (item.localidad && item.localidad.departamento && item.localidad.departamento.nombre) {
        console.log('✅ Encontrado en localidad.departamento.nombre:', item.localidad.departamento.nombre);
        return item.localidad.departamento.nombre;
      }
      if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
        console.log('✅ Encontrado en cliente.localidad.departamento.nombre:', item.cliente.localidad.departamento.nombre);
        return item.cliente.localidad.departamento.nombre;
      }

      console.log('❌ No se encontró departamento, retornando "-"');
      return '-';
    },
    formatearFecha(fecha) {
      if (!fecha) return '-';
      return ReportesHelpers.formatearFecha(fecha);
    },
    formatearMonto(monto) {
      if (!monto || monto === null) return '-';
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
      }).format(monto);
    },
    obtenerColorEstado(estado) {
      const colores = {
        Otorgado: 'green',
        Aprobado: 'blue',
        Análisis: 'orange',
        Rechazado: 'red',
        Cancelado: 'grey',
        Desistido: 'brown',
      };
      return colores[estado] || 'grey';
    },
    obtenerColorMorosidad(estado) {
      const colores = {
        'EN MORA': 'red',
        'S/E': 'grey',
        'Al día': 'green',
      };
      return colores[estado] || 'grey';
    },
    obtenerColorDiasMora(dias) {
      if (!dias) return 'grey';
      if (dias <= 30) return 'orange';
      if (dias <= 60) return 'deep-orange';
      return 'red';
    },
    calcularPorcentajeMora(montoMora, capitalVencido) {
      if (!montoMora || !capitalVencido || capitalVencido === 0) return 0;
      return (montoMora / capitalVencido) * 100;
    },
    formatearPorcentaje(porcentaje) {
      if (porcentaje === 0) return '0%';
      return `${porcentaje.toFixed(2)}%`;
    },
    calcularMontoCredito(monto, montoAnr) {
      if (!monto || monto === null) return '-';
      const montoCredito = monto - (montoAnr || 0);
      return this.formatearMonto(montoCredito);
    },
    calcularTotalMontoCredito(totalMonto, totalMontoAnr) {
      if (!totalMonto || totalMonto === null) return '-';
      const totalCredito = totalMonto - (totalMontoAnr || 0);
      return this.formatearMonto(totalCredito);
    },
    formatearDiasMora(dias) {
      if (!dias || dias === null || dias === 0) return '0 días';
      return `${Math.round(dias)} días-Promedio`;
    },
  },
};
</script>

<style scoped>
.subtotal-label {
  font-weight: bold;
  color: #1976d2;
}

.persona-text {
  color: #000000;
  font-weight: normal;
}

.estado-text {
  color: #000000;
  font-weight: normal;
}

.legajo-link {
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
}

.legajo-link:hover {
  color: #1565c0;
  text-decoration: underline;
}
</style>
